import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Gavel, Award, AlertTriangle, Sparkles, RefreshCw } from 'lucide-react';
import './AIJudgeBanner.css';

const AIJudgeBanner = ({ caseId, ruling = null, onRulingRendered }) => {
  const { getIdToken } = useAuth();
  const [currentRuling, setCurrentRuling] = useState(ruling);
  const [loading, setLoading] = useState(false);

  const handleSummonJudge = async () => {
    try {
      setLoading(true);
      const token = await getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const res = await axios.post(
        `${apiUrl}/api/court/judge-ruling`,
        { caseId },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (res.data.success && res.data.ruling) {
        setCurrentRuling(res.data.ruling);
        toast.success('Order in the Court! The Presiding Judge has rendered a decree.');
        if (onRulingRendered) onRulingRendered(res.data.ruling);
      }
    } catch (err) {
      console.error('Error summoning AI Judge:', err);
      toast.error(err.response?.data?.error || 'The Magistrate refused to take the bench. Try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!currentRuling) {
    return (
      <div className="judge-summon-card">
        <div className="summon-icon-wrapper">
          <Gavel size={32} className="gavel-summon-icon" />
        </div>
        <div className="summon-text">
          <h4>The Bench is Awaiting Deliberation</h4>
          <p>No official judicial decree has been handed down on this case yet.</p>
        </div>
        <button
          className="btn-summon-gavel"
          onClick={handleSummonJudge}
          disabled={loading}
        >
          {loading ? (
            <>
              <RefreshCw size={16} className="spin" />
              The Magistrate is Reviewing Evidence...
            </>
          ) : (
            <>
              <Sparkles size={16} />
              Strike the Gavel (AI Judge Ruling)
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="ai-judge-banner">
      <div className="judge-banner-header">
        <div className="gavel-seal">
          <Gavel size={24} />
        </div>
        <div className="judge-header-info">
          <span className="judge-title-tag">The Kangaroo Court • Official Judicial Decree</span>
          <h2 className="official-verdict-title">{currentRuling.officialVerdict}</h2>
        </div>
        <span className="judge-signature-stamp">SEAL OF THE COURT</span>
      </div>

      <div className="judge-summary-strip">
        <strong>Case Summary:</strong> {currentRuling.summary}
      </div>

      <div className="judge-roast-content">
        <h4>The Magistrate's Roast & Opinion</h4>
        <div className="roast-paragraphs">
          {typeof currentRuling.roast === 'string' ? (
            currentRuling.roast.split('\n\n').map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))
          ) : (
            <p>{JSON.stringify(currentRuling.roast)}</p>
          )}
        </div>
      </div>

      <div className="judge-footer-grid">
        {currentRuling.theSentence && (
          <div className="judge-sentence-box">
            <div className="sentence-title">
              <AlertTriangle size={16} />
              <span>Court-Ordered Sentence / Damages</span>
            </div>
            <p className="sentence-text">{currentRuling.theSentence}</p>
          </div>
        )}

        {currentRuling.bestJurorRoast && (
          <div className="judge-best-roast-box">
            <div className="best-roast-title">
              <Award size={16} />
              <span>Chief Juror Distinction Award</span>
            </div>
            <p className="best-roast-text">"{currentRuling.bestJurorRoast}"</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIJudgeBanner;
